import {
  DueDisplayOptions,
  ID,
  PrecomputedCompletion,
  RelationshipUpdate,
  Task,
  Timestamp,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import { getOverdueTasksFromTree, onDeadlineReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';

import * as Styled from './deadline-grouping-tasks-list.style';
import {
  MultipleDeadlinesRenderData,
  MultipleDeadlinesRenderDataContext,
} from './multiple-deadlines-render-context';
import { MultipleDeadlinesRenderItem } from './multiple-deadlines-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

export type MultipleDeadlinesGroupTaskListProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: UpdateTask[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  weekStartsOn: WeekDays;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  completionData?: Record<string, PrecomputedCompletion>;
};

export const MultipleDeadlinesGroupTaskList: React.FC<
  MultipleDeadlinesGroupTaskListProps
> = ({
  treeData,
  tasks,
  onReorder,
  weekStartsOn,
  collapsedSections = [],
  onSectionCollapse,
  scrollElement,
  denseFormat,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
  completionData,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDeadlineReorder({
      updatedItems,
      oldItems: Object.values(treeData),
      tasksMap,
      today,
      weekStartsOn,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );

  const renderData = useMemo<MultipleDeadlinesRenderData>(
    () => ({
      tasksMap,
      tasks,
      today,
      denseFormat,
      defaultNewTaskDate,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
      completionData,
      overdueTasks,
    }),
    [
      tasksMap,
      tasks,
      today,
      denseFormat,
      defaultNewTaskDate,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
      completionData,
      overdueTasks,
    ],
  );

  const tree = useTreeValue();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <MultipleDeadlinesRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={MultipleDeadlinesRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </MultipleDeadlinesRenderDataContext.Provider>
    </Styled.Container>
  );
};
